import type { OverviewSegment } from '@/api/worldCup';
import type { DashboardConfig } from '@/features/dashboard/config';
import { ChartCard } from '../ui/ChartCard';
import styles from './style.module.css';

type Props = {
  segments: OverviewSegment[];
  brandColors: Record<string, string>;
  hide?: DashboardConfig['hide'];
};

type ColumnKey = 'brand' | 'pack' | 'weeks' | 'game' | 'revenue' | 'users' | 'plays' | 'missions' | 'itemsPerTx';

// Maps a pack label to its module-scoped badge class. Unknown values fall back
// to the muted Kiosks style so new regions render without a CSS change.
const PACK_CLASS: Record<string, string> = {
  Stadium: styles.packStadium,
  Kiosks: styles.packKiosks,
  Gold: styles.packGold,
  Silver: styles.packSilver,
  Carrefour: styles.packCarrefour,
};

// Each column declares its share of the table width. The visible columns'
// shares are renormalised to 100% so widths stay sensible whether we show
// 5 columns (engagement-only) or 7 (full sales).
//
// Sales markets (Brazil) show `revenue` / `users` / `itemsPerTx` from the CSV
// feed. Engagement-only markets (France) hide those and instead show `plays`
// + `missions`, both sourced from games_missions.games_by_brand and merged
// into segments by DashboardPage. The visibility logic below picks the set.
const COLUMNS: { key: ColumnKey; share: number }[] = [
  { key: 'brand', share: 22 },
  { key: 'pack', share: 12 },
  { key: 'weeks', share: 12 },
  { key: 'game', share: 18 },
  { key: 'revenue', share: 14 },
  { key: 'users', share: 10 },
  { key: 'plays', share: 11 },
  { key: 'missions', share: 11 },
  { key: 'itemsPerTx', share: 12 },
];

// Parse the first ISO week number out of a "W22-W25" / "W22, W23" / "W27" string.
// Returns Infinity for missing / unparseable values so they sink to the bottom.
function startWeek(weeks: string | undefined): number {
  if (!weeks) return Infinity;
  const m = /W(\d+)/.exec(weeks);
  return m ? Number(m[1]) : Infinity;
}

export function SegmentTable({ segments, brandColors, hide = {} }: Props) {
  // Stable sort by campaign start week (ascending). Brands sharing a start
  // week stay in the order the backend / synth helper produced them.
  const sorted = [...segments].sort((a, b) => startWeek(a.weeks) - startWeek(b.weeks));
  // Engagement mode: when sales fields are all hidden, swap the sales columns
  // (Revenue / Users / Items-per-tx) for the engagement pair Plays + Missions,
  // both sourced from games_by_brand. Plays/Missions only render in this mode.
  const engagementMode = !!hide.revenue;
  const visible = COLUMNS.filter((c) => {
    if (c.key === 'revenue' && hide.revenue) return false;
    if (c.key === 'itemsPerTx' && hide.itemsPerTx) return false;
    if (c.key === 'users' && engagementMode) return false;
    if ((c.key === 'plays' || c.key === 'missions') && !engagementMode) return false;
    return true;
  });
  const totalShare = visible.reduce((s, c) => s + c.share, 0);
  const widthFor = (key: ColumnKey) => `${(visible.find((c) => c.key === key)!.share / totalShare) * 100}%`;
  const shows = (key: ColumnKey) => visible.some((c) => c.key === key);

  const unitParts = engagementMode
    ? ['Plays', 'Missions']
    : (['Revenue', 'Users', 'Items / tx'] as const).filter((label) => !(label === 'Revenue' && hide.revenue) && !(label === 'Items / tx' && hide.itemsPerTx));

  return (
    <ChartCard title="Per-brand performance" unit={unitParts.join(' · ')}>
      <table className={styles.table}>
        <colgroup>
          {visible.map((c) => (
            <col key={c.key} style={{ width: widthFor(c.key) }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th>Brand</th>
            <th>Pack</th>
            <th>Weeks</th>
            <th>Game</th>
            {shows('revenue') && <th className={styles.num}>Revenue</th>}
            {shows('users') && <th className={styles.num}>Users</th>}
            {shows('plays') && <th className={styles.num}>Plays</th>}
            {shows('missions') && <th className={styles.num}>Missions</th>}
            {shows('itemsPerTx') && <th className={styles.num}>Items / tx</th>}
          </tr>
        </thead>
        <tbody>
          {sorted.map((s) => (
            <tr key={s.brand_id}>
              <td>
                <span className={styles.nameCell}>
                  <span className={styles.dot} style={{ background: brandColors[s.brand_id] ?? 'var(--ink)' }} />
                  <span className={styles.brandName}>{s.brand_name}</span>
                </span>
              </td>
              <td>
                <span className={`${styles.pack} ${PACK_CLASS[s.pack] ?? styles.packKiosks}`}>{s.pack}</span>
              </td>
              <td>
                <span className={styles.week}>{s.weeks}</span>
              </td>
              <td>
                {s.game ? (
                  <span className={styles.game}>{s.game}</span>
                ) : (
                  <span className="muted" style={{ fontSize: 11 }}>
                    —
                  </span>
                )}
              </td>
              {shows('revenue') && <td className={`${styles.num} ${styles.value}`}>{s.revenue?.display ?? '—'}</td>}
              {shows('users') && <td className={`${styles.num} ${styles.value}`}>{s.users?.display ?? '—'}</td>}
              {shows('plays') && <td className={`${styles.num} ${styles.value}`}>{s.plays?.display ?? '—'}</td>}
              {shows('missions') && <td className={`${styles.num} ${styles.value}`}>{s.missions?.display ?? '—'}</td>}
              {/* `display` is "3.33 items / txn" — the column header already conveys the unit, so render the bare number for a tight single line. */}
              {shows('itemsPerTx') && <td className={`${styles.num} ${styles.value}`}>{s.avg_basket?.value.toFixed(2) ?? '—'}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </ChartCard>
  );
}
